import { useEffect } from 'react';
import axios from 'axios';
import { useAlert } from 'react-alert';

/**
 * The safety net for entitlement enforcement: any request the server refuses
 * with a plan-gating 403 (`feature_locked` / `feature_limit`) — including
 * paths that have no bespoke UI yet — surfaces the server's own friendly
 * message as a toast instead of a silent failure or a raw error.
 *
 * Renders nothing; mounted once inside the AlertProvider in App.jsx.
 */
import { GATING_CODES } from '../core/errors/catalogue.js';
import { safeText } from '../core/errors/sanitise.js';

export function EntitlementErrorListener() {
  const alert = useAlert();

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const data = error?.response?.data;
        if (error?.response?.status === 403 && GATING_CODES.includes(data?.code)) {
          // The server's upgrade copy is written for merchants, so it is
          // preferred — but sanitised like every other message, so a gating
          // path that 500s internally cannot print an exception here.
          const message = safeText(
            data.message,
            'This feature is not included in your store\'s current plan.',
          );

          alert.error(message);
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(id);
  }, [alert]);

  return null;
}

export default EntitlementErrorListener;
